import { createSlice } from "@reduxjs/toolkit"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import { COMPARE_IDS } from "../../../Components/Filters/CompareSelect/presets"
import { logout } from "../../LoginPage/redux/auth.actions"
import { DEFLECT_ANALYTICS_SLICE_NAME as SLICE_NAME } from "./deflectAnalytics.constants"
import { SECTION_THUNKS } from "./deflectAnalytics.thunks"

/* ------------- Initial State ------------- */

// The calendar, the comparison and the program scope the Analytics page and its inner pages open on.
export const DEFAULT_DEFLECT_FILTERS = {
  // "Last 4 weeks", the same preset the Prevent overview opens on.
  period: "27 days",
  compare: { mode: COMPARE_IDS.NONE },
  lastCompare: null,
  // Program codes (cc | oi). Empty = both.
  program: []
}

// Section responses by store key (DEFLECT_SECTION). `requestIds` holds the latest request per
// key: a response that is not the latest one arrives after the merchant has moved on and is
// dropped instead of overwriting the newer answer. `owners` holds the page (an inner page's route,
// none for the Analytics page) that last asked for each key.
const initialState = {
  filters: { ...DEFAULT_DEFLECT_FILTERS },
  // Each inner page's own window by its route (RCA-12-01): copied from `filters` when the page is
  // opened from the Analytics page, the default until then, and never written back to `filters`.
  pageFilters: {},
  data: {},
  // The business account `data` was fetched for.
  dataAccountId: null,
  fetching: {},
  requestIds: {},
  owners: {}
}

/* ------------- Slice ------------- */

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    changeDeflectFilters(state, { payload }) {
      state.filters = { ...state.filters, ...(payload ?? {}) }
    },
    openDeflectPage(state, { payload: { page, filters } }) {
      state.pageFilters[page] = filters
    },
    changeDeflectPageFilters(state, { payload: { page, patch } }) {
      state.pageFilters[page] = {
        ...(state.pageFilters[page] ?? DEFAULT_DEFLECT_FILTERS),
        ...patch
      }
    },
    // Another account's answers must not linger under the spinners, so its sections start from
    // skeletons again — also when the page opens on an account other than the one the data was
    // fetched for. `requestIds` stays: the requests already in flight are the new account's.
    resetDeflectData(state, { payload: accountId }) {
      state.data = {}
      state.dataAccountId = accountId
    }
  },
  extraReducers: (builder) => {
    builder.addCase(logout, () => initialState)

    // Several keys share one endpoint (a card and its full page), so each thunk is registered
    // once and writes to whichever key the caller passed.
    new Set(Object.values(SECTION_THUNKS)).forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state, { meta }) => {
          const { key, page } = meta.arg
          // A card and its inner page share a key but not a window: the other page's answer is
          // dropped, so the section loads from its skeleton instead of showing that window.
          if (state.owners[key] !== page) {
            delete state.data[key]
            state.owners[key] = page
          }
          state.fetching[key] = true
          state.requestIds[key] = meta.requestId
        })
        .addCase(thunk.fulfilled, (state, { payload, meta }) => {
          const { key } = meta.arg
          if (state.requestIds[key] !== meta.requestId) return
          state.fetching[key] = false
          state.data[key] = payload
        })
        .addCase(thunk.rejected, (state, { meta }) => {
          const { key } = meta.arg
          if (state.requestIds[key] !== meta.requestId) return
          state.fetching[key] = false
          state.data[key] = null
        })
    })
  }
})

export const { changeDeflectFilters, openDeflectPage, changeDeflectPageFilters, resetDeflectData } =
  slice.actions

/* ------------- Selectors ------------- */

export const selectDeflectFilters = (state) => state.deflectAnalytics.filters
export const selectDeflectPageFilters = (page) => (state) =>
  state.deflectAnalytics.pageFilters[page] ?? DEFAULT_DEFLECT_FILTERS
export const selectDeflectSection = (key) => (state) => state.deflectAnalytics.data[key]
export const selectDeflectDataAccountId = (state) => state.deflectAnalytics.dataAccountId
export const selectDeflectFetching = (key) => (state) =>
  Boolean(state.deflectAnalytics.fetching[key])
// Loading with no answer yet: the section's skeleton. Once one has arrived (even an empty or a
// failed one), a refetch keeps it on screen under a spinner instead.
export const selectDeflectFirstLoad = (key) => (state) =>
  Boolean(state.deflectAnalytics.fetching[key]) && !(key in state.deflectAnalytics.data)

/* ------------- Persistence ------------- */
// Only the merchant's own window survives a visit: section data is refetched on every mount.

const PERSISTED_REDUCER_VERSION = 1

export const persistedReducer = persistReducer(
  {
    key: SLICE_NAME,
    storage,
    version: PERSISTED_REDUCER_VERSION,
    blacklist: ["data", "dataAccountId", "fetching", "requestIds", "owners", "pageFilters"],
    migrate: async (state) => {
      if (state?._persist?.version < PERSISTED_REDUCER_VERSION) return undefined
      return state
    }
  },
  slice.reducer
)

export default slice.reducer
